/**
 * Categorized application error kinds. Used by the global error interceptor
 * to pick a translation key and decide UI behavior (toast vs inline, redirect, etc.).
 */
export type AppErrorKind =
  | 'network'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'conflict'
  | 'rateLimit'
  | 'server'
  | 'unknown';

/**
 * Single typed error surfaced to components after the HTTP error interceptor
 * normalizes raw `HttpErrorResponse` instances. Components should bind
 * `userMessage` (already translated) directly to templates and never display
 * `serverMessage` unless they intentionally want to surface the raw API text.
 */
export class AppError extends Error {
  constructor(
    public readonly kind: AppErrorKind,
    public readonly status: number,
    public readonly translationKey: string,
    public readonly userMessage: string,
    public readonly serverMessage?: string,
    public readonly fieldErrors?: Record<string, string[]>,
    /** Raw response body (when present) for components that need to inspect domain-specific fields. */
    public readonly body?: unknown,
  ) {
    super(userMessage);
    this.name = 'AppError';
    Object.setPrototypeOf(this, AppError.prototype);
  }
}

/** Type guard for safe pattern matching in components. */
export function isAppError(err: unknown): err is AppError {
  return err instanceof AppError || (typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AppError');
}
